/**
 * @fileoverview Shared rules for creating a user from the page or the dialog.
 * @module components/userCreation/userCreationFormModel
 */

import {
  validateEmail,
  validateMobilePhoneNumber,
  validateRFC,
} from "../../utils/common.js";

/** How long the creation toast stays visible. */
export const SUCCESS_MESSAGE_TIMEOUT_MS = 3000;

export const CREATE_BUTTON_SPINNER_SIZE = 24;

const ERROR_BORDER_COLOR = "red";
const SELECT_BORDER_RADIUS = "8px";
const SELECT_FONT_SIZE = "16px";
const LOCKED_SELECT_BACKGROUND = "#f5f5f5";

export const VIEW_SELECT_LAYOUT = {
  height: "40px",
  marginBottom: "10px",
};

/** Dialog menu sits above the overlay. */
export const MODAL_MENU_Z_INDEX = 1001;

export const MODAL_SELECT_LAYOUT = {
  height: "30px",
  marginBottom: "4px",
  menuZIndex: MODAL_MENU_Z_INDEX,
};

const AVATAR_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Company members type into RFC. Everyone else keeps the company RFC.
 * @param {Object|null|undefined} user
 * @param {string} rfcInput
 * @returns {string}
 */
export const rfcSourceForUser = (user, rfcInput) =>
  user?.esColaborador ? rfcInput : (user?.RFC ?? "");

/**
 * Value shown in the RFC box. A missing company RFC stays blank.
 * @param {Object|null|undefined} user
 * @param {string} rfcInput
 * @returns {string}
 */
export const displayedRfc = (user, rfcInput) =>
  (!user?.esColaborador ? user?.RFC : rfcInput) ?? "";

/**
 * @param {boolean} showError
 * @returns {string}
 */
export const errorBorderColor = (showError) =>
  showError ? ERROR_BORDER_COLOR : "";

/**
 * Email and a role are required. Phone and RFC are checked only when filled.
 * @param {Object} fields
 * @returns {boolean}
 */
export const isUserCreationFormValid = ({
  correoElectronico,
  tipoDeUsuario,
  numeroCelular,
  rfcValue,
}) => {
  if (correoElectronico.trim() === "" || tipoDeUsuario === null) {
    return false;
  }
  if (!validateEmail(correoElectronico).isValid) {
    return false;
  }
  if (numeroCelular.trim() !== "") {
    const phoneResult = validateMobilePhoneNumber(numeroCelular);
    if (!phoneResult.isValid) {
      return false;
    }
  }
  if (rfcValue.trim() !== "") {
    const rfcResult = validateRFC(rfcValue);
    if (!rfcResult.isValid) {
      return false;
    }
  }
  return true;
};

/**
 * @param {File|undefined} file
 * @returns {boolean}
 */
export const isAllowedAvatarFile = (file) =>
  Boolean(file) && AVATAR_MIME_TYPES.includes(file.type);

/**
 * react-select chrome for the role list. Height and menu stacking differ by surface.
 * @param {boolean} userTypeLocked
 * @param {{ height: string, marginBottom: string, menuZIndex?: number }} layout
 * @returns {Object}
 */
export const userTypeSelectStyles = (userTypeLocked, layout) => {
  const styles = {
    control: (baseStyles) => ({
      ...baseStyles,
      borderRadius: SELECT_BORDER_RADIUS,
      width: "100%",
      height: layout.height,
      fontSize: SELECT_FONT_SIZE,
      cursor: userTypeLocked ? "not-allowed" : "pointer",
      marginBottom: layout.marginBottom,
      borderColor: baseStyles.borderColor,
      backgroundColor: userTypeLocked
        ? LOCKED_SELECT_BACKGROUND
        : baseStyles.backgroundColor,
    }),
    container: (baseStyles) => ({
      ...baseStyles,
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
    }),
    input: (baseStyles) => ({
      ...baseStyles,
    }),
    option: (baseStyles) => ({
      ...baseStyles,
      cursor: "pointer",
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  if (layout.menuZIndex) {
    styles.menu = (baseStyles) => ({
      ...baseStyles,
      zIndex: layout.menuZIndex,
    });
    styles.menuPortal = (baseStyles) => ({
      ...baseStyles,
      zIndex: layout.menuZIndex,
    });
  }

  return styles;
};
